import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar,
  ChevronDown,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../utils/cn';

export type DateRangePreset = {
  label: string;
  value: string;
  getDates: () => { startDate: Date; endDate: Date };
};

interface DateRangePickerProps {
  value?: { startDate: Date | null; endDate: Date | null };
  onChange?: (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => void;
  presets?: DateRangePreset[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface DatePickerState {
  isOpen: boolean;
  tempStartDate: Date | null;
  tempEndDate: Date | null;
  hoveredDate: Date | null;
  selectedPreset: string | null;
  displayMonth: number;
  displayYear: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value = { startDate: null, endDate: null },
  onChange,
  presets,
  placeholder = 'Select date range',
  className,
  disabled = false,
}) => {
  const currentDate = new Date();
  const [state, setState] = useState<DatePickerState>({
    isOpen: false,
    tempStartDate: value.startDate,
    tempEndDate: value.endDate,
    hoveredDate: null,
    selectedPreset: null,
    displayMonth: currentDate.getMonth(),
    displayYear: currentDate.getFullYear(),
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const DROPDOWN_MIN_WIDTH = 340;
  const VIEWPORT_PADDING = 46;

  // Built-in preset options (can be extended via `presets` prop)
  const defaultPresetOptions = useMemo<DateRangePreset[]>(
    () => [
      {
        label: 'Last 7 days',
        value: '7days',
        getDates: () => {
          const today = new Date();
          const endDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );
          const startDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - 6
          );
          return { startDate, endDate };
        },
      },
      {
        label: 'Last 30 days',
        value: '30days',
        getDates: () => {
          const today = new Date();
          const endDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );
          const startDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - 29
          );
          return { startDate, endDate };
        },
      },
      {
        label: 'Last 12 weeks',
        value: '12weeks',
        getDates: () => {
          const today = new Date();
          const endDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );
          const startDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - (12 * 7 - 1)
          );
          return { startDate, endDate };
        },
      },
      {
        label: 'Last 12 months',
        value: '12months',
        getDates: () => {
          const today = new Date();
          const endDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );
          const startDate = new Date(
            today.getFullYear(),
            today.getMonth() - 12,
            today.getDate() + 1
          );
          return { startDate, endDate };
        },
      },
    ],
    []
  );

  const presetOptions = useMemo<DateRangePreset[]>(() => {
    if (presets) {
      return presets;
    }

    return defaultPresetOptions;
  }, [defaultPresetOptions, presets]);

  // Helper function to update state
  const updateState = useCallback((updates: Partial<DatePickerState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Position dropdown when open (for portal), keeping it within viewport horizontally
  const updateDropdownPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const dropdownWidth =
        dropdownContentRef.current?.getBoundingClientRect().width ??
        DROPDOWN_MIN_WIDTH;

      let left = rect.left;
      // Prevent overflow on the right
      if (left + dropdownWidth > viewportWidth - VIEWPORT_PADDING) {
        left = viewportWidth - dropdownWidth - VIEWPORT_PADDING;
      }
      // Prevent overflow on the left
      if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
      }

      setDropdownPosition({
        top: rect.bottom + 4,
        left,
      });
    } else {
      setDropdownPosition(null);
    }
  }, []);

  useLayoutEffect(() => {
    if (state.isOpen) {
      updateDropdownPosition();
    } else {
      setDropdownPosition(null);
    }
  }, [state.isOpen, updateDropdownPosition]);

  // Re-measure position after dropdown is mounted (so we use actual width)
  useEffect(() => {
    if (!state.isOpen || !dropdownContentRef.current) return;
    const raf = requestAnimationFrame(() => {
      updateDropdownPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [state.isOpen, updateDropdownPosition]);

  // Update position on scroll/resize when open
  useEffect(() => {
    if (!state.isOpen) return;
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [state.isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside (trigger or portal content)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideTrigger =
        dropdownRef.current && dropdownRef.current.contains(target);
      const isInsideDropdown =
        dropdownContentRef.current &&
        dropdownContentRef.current.contains(target);
      if (!isInsideTrigger && !isInsideDropdown) {
        updateState({ isOpen: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [updateState]);

  // Update temp dates when value changes and check preset match
  useEffect(() => {
    const findMatchingPreset = () => {
      if (!value.startDate || !value.endDate) return null;
      return presetOptions.find((preset) => {
        const { startDate, endDate } = preset.getDates();
        return (
          startDate.getTime() === value.startDate!.getTime() &&
          endDate.getTime() === value.endDate!.getTime()
        );
      });
    };

    updateState({
      tempStartDate: value.startDate,
      tempEndDate: value.endDate,
      selectedPreset: findMatchingPreset()?.value || null,
    });
  }, [value, presetOptions, updateState]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Normalize to local "date-only" (midnight) to avoid time drifting
  // when values are created elsewhere or serialized/deserialized.
  const normalizeDate = useCallback((date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);

  const formatDisplayValue = () => {
    if (value.startDate && value.endDate) {
      return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
    } else if (value.startDate) {
      return formatDate(value.startDate);
    }
    return placeholder;
  };

  const handleDateClick = useCallback(
    (date: Date) => {
      const { tempStartDate, tempEndDate } = state;
      const normalizedClicked = normalizeDate(date);

      if (!tempStartDate || (tempStartDate && tempEndDate)) {
        // Start new selection
        updateState({
          tempStartDate: normalizedClicked,
          tempEndDate: null,
          selectedPreset: null,
        });
      } else {
        // Complete selection
        const normalizedTempStart = normalizeDate(tempStartDate);
        const [start, end] =
          normalizedClicked < normalizedTempStart
            ? [normalizedClicked, normalizedTempStart]
            : [normalizedTempStart, normalizedClicked];
        updateState({
          tempStartDate: start,
          tempEndDate: end,
          selectedPreset: null,
        });
      }
    },
    [state, updateState, normalizeDate]
  );

  const handleApply = useCallback(() => {
    const { tempStartDate, tempEndDate } = state;
    if (tempStartDate && tempEndDate) {
      onChange?.({
        startDate: normalizeDate(tempStartDate),
        endDate: normalizeDate(tempEndDate),
      });
      updateState({ isOpen: false });
    }
  }, [state, onChange, updateState, normalizeDate]);

  const handleClear = useCallback(() => {
    updateState({
      tempStartDate: null,
      tempEndDate: null,
      selectedPreset: null,
      isOpen: false,
    });
    onChange?.({ startDate: null, endDate: null });
  }, [onChange, updateState]);

  const handlePresetSelect = useCallback(
    (preset: DateRangePreset) => {
      const { startDate, endDate } = preset.getDates();
      const normalizedStart = normalizeDate(startDate);
      const normalizedEnd = normalizeDate(endDate);
      updateState({
        tempStartDate: normalizedStart,
        tempEndDate: normalizedEnd,
        selectedPreset: preset.value,
        displayMonth: normalizedStart.getMonth(),
        displayYear: normalizedStart.getFullYear(),
      });
    },
    [updateState, normalizeDate]
  );

  const isDateInRange = useCallback(
    (date: Date) => {
      const { tempStartDate, tempEndDate } = state;
      if (!tempStartDate) return false;
      if (!tempEndDate)
        return (
          normalizeDate(date).getTime() ===
          normalizeDate(tempStartDate).getTime()
        );

      const normalizedDate = normalizeDate(date);
      const normalizedStart = normalizeDate(tempStartDate);
      const normalizedEnd = normalizeDate(tempEndDate);

      return (
        normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd
      );
    },
    [state, normalizeDate]
  );

  const isDateHovered = useCallback(
    (date: Date) => {
      const { tempStartDate, tempEndDate, hoveredDate } = state;
      if (!tempStartDate || tempEndDate || !hoveredDate) return false;
      return date >= tempStartDate && date <= hoveredDate;
    },
    [state]
  );

  const isDateInHoverRange = useCallback(
    (date: Date) => {
      const { tempStartDate, tempEndDate, hoveredDate } = state;
      if (!tempStartDate || tempEndDate || !hoveredDate) return false;
      const start = tempStartDate < hoveredDate ? tempStartDate : hoveredDate;
      const end = tempStartDate < hoveredDate ? hoveredDate : tempStartDate;
      return date >= start && date <= end;
    },
    [state]
  );

  const generateCalendarDays = useCallback((year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, []);

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const navigateMonth = useCallback(
    (direction: 'prev' | 'next') => {
      const { displayMonth, displayYear } = state;
      if (direction === 'prev') {
        if (displayMonth === 0) {
          updateState({ displayMonth: 11, displayYear: displayYear - 1 });
        } else {
          updateState({ displayMonth: displayMonth - 1 });
        }
      } else {
        if (displayMonth === 11) {
          updateState({ displayMonth: 0, displayYear: displayYear + 1 });
        } else {
          updateState({ displayMonth: displayMonth + 1 });
        }
      }
    },
    [state, updateState]
  );

  const calendarDays = useMemo(
    () => generateCalendarDays(state.displayYear, state.displayMonth),
    [state.displayYear, state.displayMonth, generateCalendarDays]
  );

  const dropdownPanel = state.isOpen && dropdownPosition && (
    <div
      ref={dropdownContentRef}
      className="absolute bg-popover border border-border rounded-xl shadow-lg z-[9999] p-4 min-w-[340px]"
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
      }}
    >
      {/* Preset Options */}
      <div className="mb-4">
        <div className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
          Quick Select
        </div>
        <div className="grid grid-cols-2 gap-2">
          {presetOptions.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePresetSelect(preset)}
              className={cn(
                'flex items-center justify-center px-3 py-2.5 text-sm rounded-lg transition-all duration-200 font-medium',
                state.selectedPreset === preset.value
                  ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover'
                  : 'bg-muted text-foreground hover:bg-accent hover:text-accent-foreground border border-border hover:text-primary-foreground'
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border mb-4">
        <div className="text-center -mt-3.5">
          <span className="bg-popover px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            or select custom dates
          </span>
        </div>
      </div>

      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => navigateMonth('prev')}
          className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="text-sm font-semibold text-foreground">
          {monthNames[state.displayMonth]} {state.displayYear}
        </h3>
        <button
          type="button"
          onClick={() => navigateMonth('next')}
          className="p-1.5 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-0.5 mb-3">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
          <div
            key={day}
            className="text-xs font-medium text-muted-foreground text-center py-1.5"
          >
            {day}
          </div>
        ))}
        {calendarDays.map((date, index) => {
          const isCurrentMonth = date.getMonth() === state.displayMonth;
          const isToday = date.toDateString() === currentDate.toDateString();
          const isSelected = isDateInRange(date);
          const isHovered = isDateHovered(date);
          const isInHoverRange = isDateInHoverRange(date);
          const normalizedDate = normalizeDate(date);
          const normalizedStart = state.tempStartDate
            ? normalizeDate(state.tempStartDate)
            : null;
          const normalizedEnd = state.tempEndDate
            ? normalizeDate(state.tempEndDate)
            : null;

          const isStartDate =
            normalizedStart &&
            normalizedDate.getTime() === normalizedStart.getTime();
          const isEndDate =
            normalizedEnd &&
            normalizedDate.getTime() === normalizedEnd.getTime();
          const isInRange =
            normalizedStart &&
            normalizedEnd &&
            normalizedDate > normalizedStart &&
            normalizedDate < normalizedEnd;

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => updateState({ hoveredDate: date })}
              onMouseLeave={() => updateState({ hoveredDate: null })}
              className={cn(
                'w-8 h-8 text-xs rounded-lg transition-all duration-150 font-medium relative',
                !isCurrentMonth &&
                  'text-muted-foreground opacity-70 hover:opacity-100',
                isCurrentMonth &&
                  'text-foreground hover:bg-muted hover:text-foreground',
                isToday &&
                  !isSelected &&
                  'bg-primary/10 text-primary font-semibold border border-primary/30',
                isInRange && 'bg-primary/20 text-primary',
                isInHoverRange && !isSelected && 'bg-primary/10 text-primary',
                isStartDate &&
                  'bg-primary text-primary-foreground font-semibold shadow-sm',
                isEndDate &&
                  'bg-primary text-primary-foreground font-semibold shadow-sm',
                isHovered &&
                  !isSelected &&
                  !isInRange &&
                  'bg-primary/10 text-primary'
              )}
            >
              {date.getDate()}
              {isInRange && (
                <>
                  {index % 7 === 0 && (
                    <div className="absolute left-0 top-0 bottom-0 w-2 bg-primary/20 rounded-l-lg" />
                  )}
                  {index % 7 === 6 && (
                    <div className="absolute right-0 top-0 bottom-0 w-2 bg-primary/20 rounded-r-lg" />
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-4 border-t border-border">
        <button
          type="button"
          onClick={handleClear}
          className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
        >
          Clear
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => updateState({ isOpen: false })}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!state.tempStartDate || !state.tempEndDate}
            className={cn(
              'px-6 py-2 text-sm font-semibold rounded-lg transition-all duration-200',
              state.tempStartDate && state.tempEndDate
                ? 'bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            )}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && updateState({ isOpen: !state.isOpen })}
        disabled={disabled}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-card shadow-sm',
          'focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary',
          'hover:border-input transition-colors duration-200',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 shrink-0 text-muted-foreground" />
          <span
            className={cn(
              'font-medium truncate',
              value.startDate && value.endDate
                ? 'text-foreground'
                : 'text-muted-foreground'
            )}
          >
            {formatDisplayValue()}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {value.startDate && value.endDate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-muted rounded-md transition-colors text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown
            className={cn(
              'w-4 h-4 text-muted-foreground transition-transform duration-200',
              state.isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {typeof document !== 'undefined' &&
        createPortal(dropdownPanel, document.body)}
    </div>
  );
};

export default DateRangePicker;
